import { create } from "zustand";
import { supabase } from "../lib/supabase";

// Types
export interface ReactionType {
  key: string;
  emoji: string;
  label: string;
  sort_order: number;
}

export interface ReactionSummary {
  reaction_type: string;
  emoji: string;
  count: number;
}

export interface RawReaction {
  reaction_type: string;
  user_id: string;
}

interface ReactionState {
  // Reaction types (loaded once, used everywhere)
  reactionTypes: ReactionType[];
  reactionTypesLoaded: boolean;
  loadingReactionTypes: boolean;

  // Methods
  fetchReactionTypes: () => Promise<void>;
  getEmoji: (reactionKey: string) => string;
  toggleReaction: (
    entityType: "activity" | "comment" | "photo",
    entityId: string,
    reactionKey: string
  ) => Promise<{ action: "added" | "updated" | "removed"; previousReaction: string | null }>;
}

export const useReactionStore = create<ReactionState>((set, get) => ({
  reactionTypes: [],
  reactionTypesLoaded: false,
  loadingReactionTypes: false,

  fetchReactionTypes: async () => {
    if (get().reactionTypesLoaded) return; // Already loaded

    set({ loadingReactionTypes: true });

    try {
      const { data, error } = await supabase
        .from("reaction_types")
        .select("key, emoji, label, sort_order")
        .eq("is_active", true)
        .order("sort_order");

      if (error) throw error;

      set({
        reactionTypes: data || [],
        reactionTypesLoaded: true,
        loadingReactionTypes: false,
      });
    } catch (error) {
      console.error("Failed to fetch reaction types:", error);
      set({
        reactionTypes: [],
        reactionTypesLoaded: false,
        loadingReactionTypes: false,
      });
    }
  },

  getEmoji: (reactionKey: string) => {
    return get().reactionTypes.find((rt) => rt.key === reactionKey)?.emoji || "";
  },

  toggleReaction: async (entityType, entityId, reactionKey) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) {
      throw new Error("Not authenticated");
    }

    // Determine table based on entity type
    const table = entityType === "activity" ? "activity_reactions" : `${entityType}_reactions`;
    const idColumn = `${entityType}_id`;

    // Check existing reaction
    const { data: existing } = await supabase
      .from(table)
      .select("reaction_type")
      .eq(idColumn, entityId)
      .eq("user_id", user.id)
      .single();

    const previousReaction = existing?.reaction_type || null;

    if (!previousReaction) {
      // Insert new
      const { error } = await supabase.from(table).insert({
        [idColumn]: entityId,
        user_id: user.id,
        reaction_type: reactionKey,
      });

      if (error) throw error;
      return { action: "added", previousReaction };
    } else if (previousReaction === reactionKey) {
      // Remove
      const { error } = await supabase
        .from(table)
        .delete()
        .eq(idColumn, entityId)
        .eq("user_id", user.id);

      if (error) throw error;
      return { action: "removed", previousReaction };
    } else {
      // Update
      const { error } = await supabase
        .from(table)
        .update({
          reaction_type: reactionKey,
          updated_at: new Date().toISOString(),
        })
        .eq(idColumn, entityId)
        .eq("user_id", user.id);

      if (error) throw error;
      return { action: "updated", previousReaction };
    }
  },
}));

// Utility function to aggregate raw reactions from DB into ReactionSummary array
export function aggregateReactions(
  rawReactions: RawReaction[] | null,
  currentUserId: string,
  reactionTypes: ReactionType[]
): { reactions: ReactionSummary[]; user_reaction: string | null } {
  if (!rawReactions?.length) {
    return { reactions: [], user_reaction: null };
  }

  const countMap = new Map<string, number>();
  let userReaction: string | null = null;

  rawReactions.forEach((r) => {
    countMap.set(r.reaction_type, (countMap.get(r.reaction_type) || 0) + 1);
    if (r.user_id === currentUserId) {
      userReaction = r.reaction_type;
    }
  });

  const reactions: ReactionSummary[] = Array.from(countMap.entries())
    .map(([type, count]) => ({
      reaction_type: type,
      emoji: reactionTypes.find((rt) => rt.key === type)?.emoji || "",
      count,
    }))
    .sort((a, b) => {
      const aOrder = reactionTypes.find((rt) => rt.key === a.reaction_type)?.sort_order || 999;
      const bOrder = reactionTypes.find((rt) => rt.key === b.reaction_type)?.sort_order || 999;
      return aOrder - bOrder;
    });

  return { reactions, user_reaction: userReaction };
}
